// ABOUTME: Hook that loads the signed-in user's profile for the app-wide `me` object.
// ABOUTME: Only what the shell blocks on — settings and parameters are fetched by the screens that use them.

import React from "react"
import { useQuery } from "@apollo/client/react"
import { UserOldDocument } from "@/graphql/main/gql/graphql"
import { User } from "./user"

interface IProps {
  userID?: string
}

export function useUser({ userID }: IProps) {
  const [user, setUser] = React.useState<User>()

  // This query gates the whole app: nothing renders until it lands (see
  // UserProvider). It used to fetch `userSettings` and `userParameters`
  // alongside it — two more requests on every start, for data no screen ever
  // read off `me`: every consumer queries them itself with an explicit userID
  // (settings tabs, the week view, the zone helpers). Do not add a query here
  // unless the shell genuinely cannot render without it.
  const userInfo = useQuery(UserOldDocument, {
    variables: { userID: userID! },
    skip: !userID,
    fetchPolicy: "cache-and-network",
  })

  // Build the user in the render that sees a new answer, and keep the last one
  // while a refetch has none. Keyed on the answer itself, not on the query result
  // object, so an unchanged answer keeps the same instance and `me` does not churn.
  const answer = userInfo.data?.user
  const [builtFrom, setBuiltFrom] = React.useState<{ userID: string; answer: typeof answer }>()
  if (userID && answer && (builtFrom?.userID !== userID || builtFrom.answer !== answer)) {
    setBuiltFrom({ userID, answer })
    setUser(new User(userID, answer))
  }

  return {
    user,
    loading: userInfo.loading,
    error: userInfo.error,
  }
}
